const { stripTags } = require('../../stringUtils');
const {
  getContentModificationDate
} = require('../date/getContentModificationDate');
const { getTaxonomies } = require('../taxonomy/taxonomyUtils');

const getCardImage = entity =>
  entity?.promoImage?.[0]?.entity ||
  entity?.image?.entity ||
  entity?.imageEntity;

const getBackgroundImage = entity => entity?.promoImageBackground?.entity;

const getCardSponsored = entity => {
  if (entity?.sponsoredTextCard && entity?.branded) {
    return {
      text: entity.sponsoredTextCard,
      bgColor: entity.sponsoredBackgroundHub || 'black',
      url: entity.sponsoredClickThrough?.url?.path
    };
  }
  return null;
};

const getCardType = content => {
  if (content.bundle === 'article' && content.mediaType === 'shortform') {
    return 'video';
  }
  if (content.bundle === 'link') {
    return 'article';
  }
  if (content.bundle === 'vertical_slideshow_gallery') {
    return 'photo_gallery';
  }
  return content.bundle;
};

/*
The following fields are always returned:
id, type, url, title, imageEntity
*/
/**
 * @param {('xl5'|'xl4'|'xl3'|'xl2'|'xl'|'l'|'base'|'s'|'xs'|'xs2'|'xs3')} scale - xls5: 8rem, xl4: 6rem, xl3: 5rem, xl2: 4rem, xl: 3rem, l: 2.5rem, base: 2rem, s: 1.5rem, xs: 1rem, xs2: 0.5rem, xs3: 0.25rem
 */

/**
 * Map a content object to a card data structure
 *
 * @param {object} content - Content to be mapped
 * @param {('subtitle', 'bundle' | 'bgImageEntity' | 'authors' | 'date' | 'videoDuration' | 'sponsored' | 'taxonomies' | 'isAnimated' | 'cta')[]} fields - Fields to be mapped. The following fields are always returned: "id", "type", "url", "title", "imageEntity"
 * @returns {boolean}
 */
const mapContentToCardData = (
  content,
  fields = [
    'bundle',
    'bgImageEntity',
    'authors',
    'date',
    'videoDuration',
    'sponsored',
    'taxonomies',
    'isAnimated'
  ]
) => {
  const cardData = {
    id: content.id,
    type: getCardType(content),
    url:
      content.bundle === 'link'
        ? content.link?.url?.path || content.url?.path
        : content.url?.path,
    title: content.promoTitle || content.title,
    imageEntity: getCardImage(content)
  };

  if (fields.includes('subtitle')) {
    cardData.subtitle = stripTags(
      content.promoSummary?.value || content.subHeadline
    );
  }
  if (fields.includes('bundle')) {
    cardData.bundle = content.bundle;
  }
  if (fields.includes('bgImageEntity')) {
    const backgroundImage = getBackgroundImage(content);
    if (backgroundImage) {
      cardData.bgImageEntity = backgroundImage;
    }
  }
  if (fields.includes('authors') && content.authors) {
    cardData.authors = content.authors.map(author => ({
      name: author.entity.name,
      url: author.entity.path.alias,
      numberOfPublishedNodes: author.entity.numberOfPublishedNodes.count
    }));
  }
  if (fields.includes('date')) {
    cardData.date = getContentModificationDate(content);
  }
  if (fields.includes('videoDuration') && content.videoRef) {
    cardData.videoDuration = content.videoRef?.entity?.duration;
  }
  if (fields.includes('sponsored')) {
    cardData.sponsored = getCardSponsored(content);
  }
  if (fields.includes('taxonomies')) {
    cardData.taxonomies = getTaxonomies(content, {
      allowPttsWithoutHubPage: true
    });
  }
  if (fields.includes('isAnimated')) {
    cardData.isAnimated = !content.animatedPromoPictureDisabled;
  }

  return cardData;
};

const removeAttributesFromCardData = (cardData, attributes) => {
  const data = cardData;
  attributes.forEach(attribute => {
    delete data[attribute];
  });
  return data;
};

const removeTaxonomyElementsFromCardData = cardData => {
  const data = cardData;
  data.taxonomies = cardData.taxonomies?.filter(term => term.isPtt);
  return data;
};

const removeAuthorsAndDateFromCardData = cardData => {
  return removeAttributesFromCardData(cardData, ['authors', 'date']);
};

const removeAuthorsFromCardData = cardData => {
  return removeAttributesFromCardData(cardData, ['authors']);
};

const setCardCTA = cardData => {
  const data = cardData;
  switch (cardData.type) {
    case 'article': {
      data.ctaType = 'read';
      break;
    }
    case 'video': {
      data.ctaType = 'watch';
      break;
    }
    case 'photo_gallery': {
      data.ctaType = 'gallery';
      break;
    }
    case 'page': {
      data.ctaType = 'explore';
      break;
    }
    default: {
      data.ctaType = 'read';
      break;
    }
  }
  data.ctaText = cardData.cardText;
  return data;
};

module.exports = {
  getCardImage,
  getBackgroundImage,
  getCardSponsored,
  mapContentToCardData,
  removeAuthorsAndDateFromCardData,
  removeAuthorsFromCardData,
  removeAttributesFromCardData,
  removeTaxonomyElementsFromCardData,
  setCardCTA
};
